/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';
import Dropzone from 'react-dropzone';

import DropzoneUploadLabel from 'components/consumer/Dropzone/DropzoneUploadLabel';
import DropzoneUploadWrapper from 'components/consumer/Dropzone/DropzoneUploadWrapper';
import MediaPreview from './MediaPreview';

const IssueMediaUpload = ({
  actions: { updateIssueAttachments, updateRemovedMediaList },
  state: { orderCartId, removedMediaList },
}) => {
  const [mediaList, updateMediaList] = useState([]);
  const [errorMessage, updateErrorMessage] = useState(null);
  const [hideDropBox, updateHideDropBox] = useState(false);
  const maxImages = 5;
  const maxVideos = 1;

  const onFileDrop = (accepted, rejected) => {
    updateErrorMessage(null);

    if (rejected && rejected.length > 0) {
      updateErrorMessage(
        'Sorry, there was a problem with your file! ' +
          'We support .jpg, .jpeg, .png, .gif, .heic images and .mov, .mp4, .mv4, .m4v, .mpeg-4 videos up to 20mb.'
      );
      return null;
    }

    if (accepted && accepted.length > 0) {
      let imageFiles = [];
      let videoFiles = [];
      let fileList = mediaList;

      fileList = fileList?.length > 0 ? fileList.concat(accepted) : accepted;
      fileList.forEach(file => {
        if (file.type.match(/^image\/*/)) {
          imageFiles.push(
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          );
        } else if (file.type.match(/^video\/*/)) {
          videoFiles.push(file);
        }
      });
      fileList = [];

      if (imageFiles.length >= maxImages && videoFiles.length >= maxVideos) {
        updateHideDropBox(true);
      }

      if (imageFiles.length > maxImages) {
        imageFiles = imageFiles.slice(0, maxImages);
        updateErrorMessage(
          `You've reached the maximum amount of ${maxImages} images for submission.`
        );
        return null;
      }

      if (videoFiles.length > maxVideos) {
        videoFiles = videoFiles.slice(0, maxVideos);
        updateErrorMessage(`Only one video is allowed for submission.`);
        return null;
      }

      if (videoFiles.length + imageFiles.length === 0) {
        updateErrorMessage(`You must upload atleast one image or video..`);
        return null;
      }
      fileList =
        imageFiles.length > 0
          ? fileList.concat(fileList, imageFiles)
          : fileList;

      fileList =
        videoFiles.length > 0
          ? fileList.concat(fileList, videoFiles)
          : fileList;

      fileList = uniqBy(fileList, 'name');
      updateMediaList([...fileList]);
    }
    return null;
  };

  const manageMediaList = () => {
    const attachments = { photos: [], videos: [] };
    if (!Array.isArray(mediaList) || mediaList.length === 0) {
      updateIssueAttachments(orderCartId, attachments);
    }
    mediaList.forEach(file => {
      if (file.type.match(/^image\/*/)) {
        attachments.photos.push(file);
      } else if (file.type.match(/^video\/*/)) {
        attachments.videos.push(file);
      }
    });
    updateIssueAttachments(orderCartId, attachments);
  };

  const addAttachments = (id, s3Path) => {
    if (mediaList[id] && s3Path) {
      mediaList[id] = Object.assign(mediaList[id], {
        s3Path,
      });
    }
    manageMediaList();
  };

  const removeAttachment = id => {
    updateErrorMessage(null);
    removedMediaList.push(mediaList[id]);
    mediaList.splice(id, 1);
    manageMediaList();
    updateMediaList([...mediaList]);
    updateRemovedMediaList([...removedMediaList]);
    updateHideDropBox(false);
  };

  return (
    <div className="mt-6">
      {mediaList && mediaList.length > 0 && (
        <div className="m-0 grow max-w-full basis-0">
          <ul className="m-0 py-2 relative list-none">
            {mediaList.map((file, id) => (
              <MediaPreview
                key={file.path}
                actions={{
                  removeAttachment,
                  addAttachments,
                }}
                state={{ file, id }}
              />
            ))}
          </ul>
        </div>
      )}

      {!!errorMessage && (
        <div
          data-xc="warranty-form-validation"
          className="py-2 px-3.5 my-4 border-[1px] border-[#d00] bg-[#FFEBE8] text-sm"
        >
          {errorMessage}
        </div>
      )}

      {!hideDropBox && (
        <DropzoneUploadWrapper>
          <Dropzone
            accept=".jpg, .jpeg, .png, .gif, .mov, .mp4, .mv4, .m4v, .mpeg4, .heic"
            multiple
            maxSize={2e7}
            onDrop={onFileDrop}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps()}
                className={
                  isDragActive
                    ? 'border-[1px] border-brand-darker border-dotted'
                    : ''
                }
              >
                <input autoCorrect="off" {...getInputProps()} />
                <DropzoneUploadLabel subLabel=", 5 photos & 1 video maximum." />
              </div>
            )}
          </Dropzone>
        </DropzoneUploadWrapper>
      )}
    </div>
  );
};

IssueMediaUpload.propTypes = {
  actions: PropTypes.shape({
    updateIssueAttachments: PropTypes.func,
    updateRemovedMediaList: PropTypes.func,
  }),
  state: PropTypes.shape({
    orderCartId: PropTypes.string,
    removedMediaList: PropTypes.array,
  }),
};

export default React.memo(IssueMediaUpload);
